import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useRef,
  memo,
  useCallback,
} from "react";
import {
  ManagedWindow,
  closeWindow,
  openWindow,
  WindowId,
} from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import {
  ADModalBody,
  AdvancedDragModal,
} from "src/componentsUtils/AdvancedDragModal";
import { JPBManager } from "./JPBManager";
import type { JPBDateBounds } from "./JPBManager";
import {
  JPBReferenceProperty,
  YearGrouping,
  DEFAULT_ASSUMPTIONS,
  AreaUnit,
  PriceMode,
  JPBReportData,
  JPBAssumptions,
  AverageCalculationMethod,
  JPBPriceIndexSource,
  JPBReportPolygonAnalysis,
} from "./JPBState";
import { JPBHistogram } from "./JPBHistogram";
import { JPBTable, JPBTableRef } from "./JPBTable";
import {
  JPBSearchArea,
  addSearchAreaOutline,
  removeSearchAreaLayer,
  setSearchAreaGeometry,
} from "./JPBSearchArea";
import { JPBSimpleSearchHeader } from "./JPBSimpleSearchHeader";
import { JPBStatistics } from "./JPBStatistics";
import { JPBPolygonAnalyzer } from "./JPBPolygonAnalyzer";
import {
  Localization,
  SettingsFixedPriorities,
  SettingsManager,
  SessionContext,
} from "@viamap/viamap2-common";
import { getCheckedItemsFromCurrentAssumptions } from "./JPBFunc";
import {
  MapitStateContext,
  actionSetMapInteractionState,
  MapInteractionState,
  type LayersType,
} from "src/states/MapitState";
import type { MapFacadeMaplibre } from "src/managers/MapFacadeMaplibre";
import type { GeoJSONSource } from "maplibre-gl";
import { feature } from "@turf/helpers";
import {
  bboxPolygon,
  difference,
  featureCollection,
  buffer
} from "@turf/turf";
import { AddressInterface } from "src/managers/AddressInterface";
import "../../css/propertyInfoStyle.css";
import { LayerVariabilityType, LayerType } from "src/common/managers/Types";
import {
  setupJPBMapLayers,
  removeJPBMapLayers,
  updateFeatureStates,
  updateJPBHoverLabelsData,
  SOURCE_ID,
  SOURCE_ID_HOVER,
  SOURCE_ID_POLY,
  LAYER_ID,
  LAYER_ID_ISSUE,
  LAYER_ID_BUILDING,
  LAYER_ID_POLY,
  LAYER_ID_FARMLAND,
  LAYER_ID_JORDBUNDSKORT,
  SOURCE_ID_JORDBUNDSKORT,
  highlightPolygonOnMap,
  createJordbundskortGeoJson,
} from "./JPBMapLayers";
import { Spinner } from "react-bootstrap";
import { handleGenerateJPBRapport } from "./JPBRepportGen";
import {
  renderJPBOverviewWithJordbundImage,
  renderJPBSubjectPolygonImage,
  renderJPBAllPointsMapImage,
  renderJPBHeatmapImage,
  renderClonedElementImage,
  serializeHistogramSvg,
  renderHistogramFromSerialized,
} from "./JPBMapSnapshot";
import { JPBCaptureStage } from "./JPBCaptureStage";
import { resolveJpbReportSubjectInfo } from "./JPBSubjectInfo";
import { ProtectedFeature } from "src/components/ProtectedFeature";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { ActionTracker } from "src/common/managers/ActionTracker";
import { MapitUtils } from "src/managers/MapitUtils";
import {
  JPB_BUFFER_POINT_PIN_COLOR,
  setJpbBufferPointLegendLayerId,
} from "./jpbBufferPinLegend";
import { message } from "src/components/Message";
import {
  clearJPBSession,
  createInitialJPBSessionState,
  JPBSessionProvider,
  useJPBSessionActions,
  useJPBSessionSlice,
} from "./JPBSessionState";
import {
  persistUserSettingsProfile,
  resetJpbUserAssumptionSettings,
} from "./jpbUserSettingsReset";
import { lengthByUserPreference } from "src/managers/PlanarCalculationMethods";

const MASK_SOURCE_ID = "jpb-search-area-mask-source";
const MASK_LAYER_ID = "jpb-search-area-mask";
/** When `"false"`, the next JPB open starts from defaults instead of persisted sidebar choices. */
const JPB_PERSIST_USER_SETTINGS_KEY = "jpb.persistUserSettings";
const JPB_PERSISTED_YEAR_GROUPING_KEY = "jpb.appliedYearGrouping";
/** Legacy key — no longer persisted; cleared on startup and when clearing saved choices. */
const JPB_LEGACY_PERSISTED_CALC_DATE_TO_KEY = "jpb.appliedCalculationDateTo";
const JPB_PERSISTED_USER_SETTING_KEYS = [
  "jpb.areaUnit",
  "jpb.priceMode",
  "jpb.averageCalculationMethod",
  "jpb.priceIndexSource",
  "jpb.viewMode",
  JPB_PERSISTED_YEAR_GROUPING_KEY,
];

function clearPersistedJPBUserSettings(): void {
  JPB_PERSISTED_USER_SETTING_KEYS.forEach((key) => localStorage.removeItem(key));
  localStorage.removeItem(JPB_LEGACY_PERSISTED_CALC_DATE_TO_KEY);
  localStorage.removeItem("jpb.persistedAssumptions");
  sessionStorage.removeItem("jpb.session.hydratedAssumptions");
}

function toStartOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

const YEAR_GROUPING_PERSIST_VALUES = new Set<number>([
  YearGrouping.OneYear,
  YearGrouping.TwoYears,
  YearGrouping.ThreeYears,
  YearGrouping.FourYears,
  YearGrouping.FiveYears,
]);

function parsePersistedYearGrouping(raw: string | null): YearGrouping | null {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return YEAR_GROUPING_PERSIST_VALUES.has(n) ? (n as YearGrouping) : null;
}

function createDefaultJPBSessionChoices(
  getDefaultAccessiblePriceIndexSource: () => JPBPriceIndexSource,
) {
  return {
    priceIndexSource: getDefaultAccessiblePriceIndexSource(),
    areaUnit: AreaUnit.SquareMeter,
    priceMode: PriceMode.Standard,
    averageCalculationMethod: AverageCalculationMethod.WeightedByArea,
    viewMode: "simple" as const,
  };
}

function createJPBInitialSessionState(
  getDefaultAccessiblePriceIndexSource: () => JPBPriceIndexSource,
  useDefaultSessionChoices: boolean,
) {
  if (useDefaultSessionChoices) {
    return createInitialJPBSessionState(
      createDefaultJPBSessionChoices(getDefaultAccessiblePriceIndexSource),
    );
  }

  const persistedPriceIndexSource = JPBManager.normalizePriceIndexSource(
    localStorage.getItem("jpb.priceIndexSource"),
  );
  const persistedYearGrouping = parsePersistedYearGrouping(
    localStorage.getItem(JPB_PERSISTED_YEAR_GROUPING_KEY),
  );
  return createInitialJPBSessionState({
    priceIndexSource:
      persistedPriceIndexSource === JPBPriceIndexSource.None
        ? getDefaultAccessiblePriceIndexSource()
        : persistedPriceIndexSource,
    areaUnit:
      (localStorage.getItem("jpb.areaUnit") as AreaUnit) ||
      AreaUnit.SquareMeter,
    priceMode:
      (localStorage.getItem("jpb.priceMode") as PriceMode) ||
      PriceMode.Standard,
    averageCalculationMethod:
      (localStorage.getItem(
        "jpb.averageCalculationMethod",
      ) as AverageCalculationMethod) ||
      AverageCalculationMethod.WeightedByArea,
    viewMode:
      localStorage.getItem("jpb.viewMode") === "advanced"
        ? "advanced"
        : "simple",
    appliedYearGrouping: persistedYearGrouping ?? undefined,
  });
}

function prepareJPBWindowOpen(): { useDefaultSessionChoices: boolean } {
  if (localStorage.getItem(JPB_PERSIST_USER_SETTINGS_KEY) !== "false") {
    return { useDefaultSessionChoices: false };
  }
  localStorage.removeItem(JPB_PERSIST_USER_SETTINGS_KEY);
  clearPersistedJPBUserSettings();
  resetJpbUserAssumptionSettings();
  return { useDefaultSessionChoices: true };
}

function withYearsOffset(date: Date, years: number): Date {
  const d = new Date(date);
  d.setFullYear(d.getFullYear() + years);
  return d;
}

function stableStringify(value: unknown): string {
  const normalize = (input: unknown): unknown => {
    if (Array.isArray(input)) {
      return input.map(normalize);
    }
    if (input && typeof input === "object") {
      const entries = Object.entries(input as Record<string, unknown>).sort(
        ([a], [b]) => a.localeCompare(b),
      );
      return entries.reduce<Record<string, unknown>>((acc, [key, val]) => {
        acc[key] = normalize(val);
        return acc;
      }, {});
    }
    return input;
  };
  return JSON.stringify(normalize(value));
}

const ADDRESS_RESOLVE_ATTEMPTS = 3;
const ADDRESS_RETRY_DELAY_MS = 300;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function resolvePropertyInfoStyleAddressWithRetry(
  lat: number,
  lng: number,
): Promise<string | null> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= ADDRESS_RESOLVE_ATTEMPTS; attempt++) {
    try {
      return await AddressInterface.resolvePropertyInfoStyleAddressFromPoint(
        lat,
        lng,
      );
    } catch (error) {
      lastError = error;
      if (attempt < ADDRESS_RESOLVE_ATTEMPTS) {
        await delay(ADDRESS_RETRY_DELAY_MS * attempt);
      }
    }
  }
  console.warn("Failed to resolve JPB subject address after retries", {
    lat,
    lng,
    error: lastError,
  });
  return null;
}

type MapitStateDispatch = React.Dispatch<any>;

type MapInfoRef = React.MutableRefObject<{ mapStyle?: any } | null>;

function JPBWindowInner(props: {
  managedWindow: ManagedWindow;
  map: MapFacadeMaplibre;
  layers: LayersType;
  dispatch: MapitStateDispatch;
  mapInfoRef: MapInfoRef;
  restoredFromSkipPersist: boolean;
}) {
  const {
    managedWindow,
    map: mapFacade,
    layers,
    dispatch: mapitStateDispatch,
    mapInfoRef,
  } = props;
  const { dispatch: windowDispatch } = useWindowContext();
  const { hasAccessToFeature } = useContext(ApplicationStateContext);
  const sessionKey = managedWindow.uid ?? managedWindow.id;
  const hasAgroCuraIndexAccess = hasAccessToFeature(
    Feature.JPBAgroCuraIndexAllow,
  );
  const hasMolbakIndexAccess = hasAccessToFeature(Feature.JPBMolbakIndexAllow);

  const getDefaultAccessiblePriceIndexSource = (): JPBPriceIndexSource => {
    if (hasMolbakIndexAccess) return JPBPriceIndexSource.Molbak;
    if (hasAgroCuraIndexAccess) return JPBPriceIndexSource.AgroCura;
    return JPBPriceIndexSource.None;
  };

  const normalizeAccessiblePriceIndexSource = (
    source: string | JPBPriceIndexSource | null | undefined,
    fallbackToDefaultWhenNone = false,
  ): JPBPriceIndexSource => {
    const normalized = JPBManager.normalizePriceIndexSource(source);
    if (normalized === JPBPriceIndexSource.None) {
      return fallbackToDefaultWhenNone
        ? getDefaultAccessiblePriceIndexSource()
        : JPBPriceIndexSource.None;
    }
    if (normalized === JPBPriceIndexSource.AgroCura && !hasAgroCuraIndexAccess)
      return getDefaultAccessiblePriceIndexSource();
    if (normalized === JPBPriceIndexSource.Molbak && !hasMolbakIndexAccess)
      return getDefaultAccessiblePriceIndexSource();
    return normalized;
  };

  const [searchState] = useJPBSessionSlice("search");
  const [filtersState] = useJPBSessionSlice("filters");
  const [resultsState] = useJPBSessionSlice("results");
  const [analyzerState] = useJPBSessionSlice("analyzer");
  const [uiState] = useJPBSessionSlice("ui");
  const { patchSearch, patchFilters, patchResults, patchUi, patchAnalyzer } =
    useJPBSessionActions();
  const { state: sessionState } = useContext(SessionContext);
  const referenceProperties = resultsState.referenceProperties;
  const loading = resultsState.loading;
  const error = resultsState.error;
  const checkedItems = filtersState.checkedItems;
  const bufferGeometry = searchState.bufferGeometry;
  const bufferDistanceKm = searchState.bufferDistanceKm;
  const bufferDistanceByLayer = searchState.bufferDistanceByLayer;
  const hideUnselected = filtersState.hideUnselected;
  const customPriceAssumptions = filtersState.customPriceAssumptions;
  const systemPriceAssumptions = filtersState.systemPriceAssumptions;
  const selectedLayer = searchState.selectedLayer;
  const jpbViewMode = searchState.viewMode;
  const jpbSimpleResolvedAddressLine = searchState.simpleResolvedAddressLine;
  const jpbSimpleAddressLineResolving = searchState.simpleAddressLineResolving;
  const areaUnit = filtersState.areaUnit;
  const priceMode = filtersState.priceMode;
  const averageCalculationMethod = filtersState.averageCalculationMethod;
  const priceIndexSource = filtersState.priceIndexSource;
  const isWindowMinimized = uiState.isWindowMinimized;
  const [persistUserSettings, setPersistUserSettings] = useState(true);
  const persistUserSettingsRef = useRef(persistUserSettings);
  persistUserSettingsRef.current = persistUserSettings;
  const persistJPBUserSetting = useCallback((key: string, value: string) => {
    if (persistUserSettingsRef.current) {
      localStorage.setItem(key, value);
    }
  }, []);
  const dateBounds = resultsState.dateBounds;
  const appliedYearGrouping = filtersState.appliedYearGrouping;
  const appliedCalculationDateTo = filtersState.appliedCalculationDateTo;
  const isGeneratingReport = resultsState.isGeneratingReport;
  const settingsRefreshToken = resultsState.settingsRefreshToken;

  useEffect(() => {
    localStorage.removeItem(JPB_LEGACY_PERSISTED_CALC_DATE_TO_KEY);
  }, []);

  useEffect(() => {
    if (!props.restoredFromSkipPersist) return;
    patchResults((prev) => ({
      settingsRefreshToken: prev.settingsRefreshToken + 1,
    }));
  }, [props.restoredFromSkipPersist, patchResults]);

  useEffect(() => {
    if (!persistUserSettings) return;
    localStorage.setItem(
      JPB_PERSISTED_YEAR_GROUPING_KEY,
      String(appliedYearGrouping),
    );
  }, [persistUserSettings, appliedYearGrouping]);

  const mapRefreshToken = resultsState.mapRefreshToken;
  const [polygonAnalysis, setPolygonAnalysis] =
    useState<JPBReportPolygonAnalysis | null>(null);
  // Mirror the latest polygonAnalysis in a ref so handleExportReport can poll it
  // after auto-triggering an analysis on the selected point layer.
  const polygonAnalysisRef = useRef<JPBReportPolygonAnalysis | null>(null);
  polygonAnalysisRef.current = polygonAnalysis;
  const analyzerStateRef = useRef(analyzerState);
  analyzerStateRef.current = analyzerState;
  const tableRef = useRef<JPBTableRef>(null);
  const tableScrollAnchorRef = useRef<HTMLDivElement>(null);
  const histogramRef = useRef<HTMLDivElement>(null);
  const statisticsRef = useRef<HTMLDivElement>(null);
  const polygonAnalyzerRef = useRef<HTMLDivElement>(null);
  const [forceStatisticsSoilExpanded, setForceStatisticsSoilExpanded] =
    useState(false);
  const checkedItemsRef = useRef<Set<string>>(checkedItems);
  const originalIconImagesRef = useRef<{ [layerId: string]: any }>({}); // Store original icon images
  const highlightCleanupRef = useRef<(() => void) | null>(null); // Store cleanup function for polygon highlight
  const mapRefreshTimeoutRef = useRef<number | null>(null);
  const lastJpbDataFetchKey = resultsState.lastFetchKey;
  checkedItemsRef.current = checkedItems;
  const setJpbSimpleResolvedAddressLine = useCallback(
    (value: string | null | ((prev: string | null) => string | null)) => {
      patchSearch((prev) => ({
        simpleResolvedAddressLine:
          typeof value === "function"
            ? value(prev.simpleResolvedAddressLine)
            : value,
      }));
    },
    [patchSearch],
  );
  const setJpbSimpleAddressLineResolving = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      patchSearch((prev) => ({
        simpleAddressLineResolving:
          typeof value === "function"
            ? value(prev.simpleAddressLineResolving)
            : value,
      }));
    },
    [patchSearch],
  );

  useEffect(() => {
    mapitStateDispatch(
      actionSetMapInteractionState(
        isWindowMinimized
          ? MapInteractionState.Normal
          : MapInteractionState.Override,
      ),
    );
  }, [isWindowMinimized, mapitStateDispatch]);

  useEffect(() => {
    return () => {
      mapitStateDispatch(
        actionSetMapInteractionState(MapInteractionState.Normal),
      );
      // Clean up any active highlight when component unmounts
      if (highlightCleanupRef.current) {
        highlightCleanupRef.current();
        highlightCleanupRef.current = null;
      }
    };
  }, [mapitStateDispatch]);

  useEffect(() => {
    const createSettingsSnapshot = () =>
      stableStringify({
        assumptions: SettingsManager.getSystemSetting(
          "jpb.assumptions",
          DEFAULT_ASSUMPTIONS,
        ),
        defaultRadiusKm: SettingsManager.getSystemSetting(
          "jpb.defaultRadiusKm",
          10,
        ),
        maxProperties: SettingsManager.getSystemSetting(
          "jpb.maxProperties",
          4000,
        ),
        indexName: SettingsManager.getSystemSetting(
          "jpb.indexName",
          "jpb_landbrugshandler_testv5",
        ),
      });

    let previousSnapshot = createSettingsSnapshot();
    const id = window.setInterval(() => {
      const nextSnapshot = createSettingsSnapshot();
      if (nextSnapshot !== previousSnapshot) {
        previousSnapshot = nextSnapshot;
        patchResults((prev) => ({
          settingsRefreshToken: prev.settingsRefreshToken + 1,
        }));
      }
    }, 1500);

    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    return () => {
      if (mapRefreshTimeoutRef.current) {
        window.clearTimeout(mapRefreshTimeoutRef.current);
        mapRefreshTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const preparedState = await JPBManager.prepareIndexedLandsdelState(
        true,
      ).catch(() => null);
      if (cancelled || !preparedState) return;
    })();

    return () => {
      cancelled = true;
    };
  }, [settingsRefreshToken]);

  useEffect(() => {
    if (!mapFacade || referenceProperties.length === 0) {
      if (mapRefreshTimeoutRef.current) {
        window.clearTimeout(mapRefreshTimeoutRef.current);
        mapRefreshTimeoutRef.current = null;
      }
      return;
    }

    if (mapRefreshTimeoutRef.current) {
      window.clearTimeout(mapRefreshTimeoutRef.current);
    }
    mapRefreshTimeoutRef.current = window.setTimeout(() => {
      patchResults((prev) => ({ mapRefreshToken: prev.mapRefreshToken + 1 }));
      mapRefreshTimeoutRef.current = null;
    }, 2000);
  }, [settingsRefreshToken, mapFacade, referenceProperties.length]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const bounds = await JPBManager.fetchDateBounds().catch(() => null);
      if (cancelled) return;

      if (!bounds?.maxDate || !bounds?.minDate) {
        return;
      }

      const maxDateCandidate = toStartOfDay(bounds.maxDate);
      const minDateCandidate = toStartOfDay(bounds.minDate);

      const normalizedBounds: JPBDateBounds = {
        minDate: minDateCandidate,
        maxDate: maxDateCandidate,
      };
      patchResults((prev) => {
        if (
          prev.dateBounds &&
          prev.dateBounds.minDate.getTime() ===
          normalizedBounds.minDate.getTime() &&
          prev.dateBounds.maxDate.getTime() ===
          normalizedBounds.maxDate.getTime()
        ) {
          return {};
        }
        return { dateBounds: normalizedBounds };
      });
      patchFilters((prev) => {
        const current = prev.appliedCalculationDateTo;
        if (current === null)
          return { appliedCalculationDateTo: maxDateCandidate };
        if (current < minDateCandidate)
          return { appliedCalculationDateTo: minDateCandidate };
        if (current > maxDateCandidate)
          return { appliedCalculationDateTo: maxDateCandidate };
        return {};
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [settingsRefreshToken]);

  useEffect(() => {
    if (selectedLayer === undefined) return;
    ActionTracker.track("JPBModule", "Action", "SelectLayerForCalculation");
  }, [selectedLayer]);

  useEffect(() => {
    const normalized = normalizeAccessiblePriceIndexSource(priceIndexSource);
    if (normalized !== priceIndexSource) {
      patchFilters({ priceIndexSource: normalized });
      persistJPBUserSetting("jpb.priceIndexSource", normalized);
    }
  }, [hasAgroCuraIndexAccess, hasMolbakIndexAccess, persistJPBUserSetting, priceIndexSource]);

  // MARK: Handlers
  const lockedDocumentIds = useMemo(() => {
    const locked = new Set<string>();
    for (const prop of referenceProperties) {
      if (
        JPBManager.getSelectionLockState(prop, customPriceAssumptions).isLocked
      ) {
        locked.add(prop.documentID);
      }
    }
    return locked;
  }, [referenceProperties, customPriceAssumptions, settingsRefreshToken]);

  const sanitizeCheckedItems = (items: Set<string>) => {
    if (lockedDocumentIds.size === 0) return items;
    const sanitized = new Set(items);
    lockedDocumentIds.forEach((id) => sanitized.delete(id));
    return sanitized;
  };

  const updateCheckedItems = (newCheckedItems: Set<string>) => {
    const sanitized = sanitizeCheckedItems(newCheckedItems);
    patchFilters({ checkedItems: sanitized });

    const map = mapFacade?.getMapPop();
    if (map && map.getSource(SOURCE_ID)) {
      referenceProperties.forEach((prop) => {
        const checked = sanitized.has(prop.documentID);
        map.setFeatureState(
          { source: SOURCE_ID, id: prop.documentID },
          { checked },
        );
        if (map.getSource(SOURCE_ID_HOVER)) {
          map.setFeatureState(
            { source: SOURCE_ID_HOVER, id: prop.documentID },
            { checked },
          );
        }
        map.setFeatureState(
          { source: SOURCE_ID_POLY, id: prop.documentID },
          { checked },
        );
      });
    }
  };

  const handleRowClick = (property: JPBReferenceProperty) => {
    if (!mapFacade) {
      console.warn("handleRowClick: mapFacade is not available");
      return;
    }
    const map = mapFacade.getMapPop();
    if (!map) {
      console.warn("handleRowClick: map is not available");
      return;
    }

    // Clear any existing highlight
    if (highlightCleanupRef.current) {
      highlightCleanupRef.current();
      highlightCleanupRef.current = null;
    }

    if (property?.geoshape) {
      try {
        const geoshapeFeature = feature(property.geoshape);
        mapFacade.zoomToDataAvoidingWindows(geoshapeFeature, 25);

        // Highlight the polygon with bright green outline
        highlightCleanupRef.current = highlightPolygonOnMap(
          map,
          property.geoshape,
          property.documentID,
          5000, // 5 seconds, matching table row highlight duration
        );
      } catch (error) {
        console.error(
          "handleRowClick: error creating feature or zooming",
          error,
        );
      }
    } else {
      try {
        const pointFeature = feature({
          type: "Point",
          coordinates: [property.coordinates.lng, property.coordinates.lat],
        });
        const bufferedFeature = buffer(pointFeature, 0.5, {
          units: "kilometers",
        });
        if (bufferedFeature) {
          mapFacade.zoomToDataAvoidingWindows(bufferedFeature, 25);

          // Highlight the buffered circle with bright green outline
          highlightCleanupRef.current = highlightPolygonOnMap(
            map,
            bufferedFeature.geometry,
            property.documentID,
            5000, // 5 seconds, matching table row highlight duration
          );
        } else {
          console.warn("handleRowClick: bufferedFeature is null");
        }
      } catch (error) {
        console.error("handleRowClick: error creating point or zooming", error);
      }
    }
  };

  const getPriceAnalysisReportFailureReason = (
    expectedLayerId: number,
  ): string | null => {
    const latestAnalyzerState = analyzerStateRef.current;
    if (latestAnalyzerState.selectedLayerId !== expectedLayerId) {
      return null;
    }
    if (
      latestAnalyzerState.isFetchingGeometry ||
      latestAnalyzerState.isCalculating
    ) {
      return null;
    }

    const result = latestAnalyzerState.analysisResult;
    if (!result || result.estimatedPricePerSquareMeter !== null) {
      return null;
    }
    if (result.error) {
      return result.error;
    }
    if (result.farmlandOverlapPercentage <= 0) {
      return Localization.getText(
        "Ejendommen har ikke noget markareal at beregne ud fra.",
      );
    }
    if (Object.keys(result.soilComposition).length === 0) {
      return Localization.getText(
        "Der kunne ikke findes jordbundsdata for markarealet.",
      );
    }
    return Localization.getText(
      "Prisen kunne ikke beregnes med de valgte referencehandler og forudsætninger.",
    );
  };

  const showPriceAnalysisReportFailure = (reason?: string | null) => {
    const baseMessage = Localization.getText(
      "Prisanalysen fejlede, kunne ikke danne rapport.",
    );
    message.error(reason ? `${baseMessage} ${reason}` : baseMessage);
  };

  const handleExportReport = async () => {
    patchResults({ isGeneratingReport: true });
    // If no polygon analysis has been produced yet, attempt to auto-trigger one
    // on the currently selected layer — but only if it's a point layer (which
    // the polygon analyzer can resolve to an SFE polygon by buffering pins to
    // their cadasters). For polygon-area selections we cannot infer a subject
    // polygon automatically, so we surface that to the user and bail out.
    if (!polygonAnalysisRef.current) {
      const layerInfo =
        selectedLayer !== undefined ? layers[selectedLayer] : undefined;
      const isPointLayer =
        !!layerInfo && MapitUtils.isPointLayer(layerInfo.type);
      if (selectedLayer === undefined || !layerInfo) {
        patchResults({ isGeneratingReport: false });
        alert(
          Localization.getText(
            "Eksportér rapport (analysér først et areal med polygon-analysen)",
          ),
        );
        return;
      }
      if (!isPointLayer) {
        patchResults({ isGeneratingReport: false });
        alert(
          Localization.getText(
            "Polygon-analysen kan kun startes automatisk for et punkt-lag. Åbn polygon-analysen og kør den manuelt for det valgte areal.",
          ),
        );
        return;
      }
      try {
        if (analyzerStateRef.current.selectedLayerId === selectedLayer) {
          patchAnalyzer({ selectedLayerId: selectedLayer });
        } else {
          patchAnalyzer({
            selectedLayerId: selectedLayer,
            analysisResult: null,
            geometryData: null,
            farmlandIntersectionGeometry: null,
            jordbundskortIntersectionGeometry: null,
            analysisPropertyOutlineGeometry: null,
          });
        }
        const startedAt = Date.now();
        const TIMEOUT_MS = 60_000;
        const POLL_MS = 200;
        let failureReason = getPriceAnalysisReportFailureReason(selectedLayer);
        while (
          !polygonAnalysisRef.current &&
          !failureReason &&
          Date.now() - startedAt < TIMEOUT_MS
        ) {
          await new Promise<void>((r) => setTimeout(r, POLL_MS));
          failureReason = getPriceAnalysisReportFailureReason(selectedLayer);
        }
        if (failureReason) {
          patchResults({ isGeneratingReport: false });
          showPriceAnalysisReportFailure(failureReason);
          return;
        }
        if (!polygonAnalysisRef.current) {
          patchResults({ isGeneratingReport: false });
          showPriceAnalysisReportFailure(
            Localization.getText(
              "Polygon-analysen kunne ikke gennemføres automatisk. Prøv igen, eller kør analysen manuelt.",
            ),
          );
          return;
        }
      } catch (e) {
        console.error("Auto polygon analysis failed", e);
        patchResults({ isGeneratingReport: false });
        showPriceAnalysisReportFailure(
          e instanceof Error ? e.message : undefined,
        );
        return;
      }
    }
    // From here on we know a fresh polygonAnalysis exists; pull it from the ref
    // for use throughout the rest of the export flow.
    const polygonAnalysisForReport = polygonAnalysisRef.current;
    try {
      const { assumptions } = getCheckedItemsFromCurrentAssumptions(
        referenceProperties,
        customPriceAssumptions,
        priceMode,
      );

      // Resolved name of the currently selected map layer, used as a fallback
      // when reverse-geocoding cannot derive a subject address from the polygon.
      let selectedLayerName: string | undefined;
      if (selectedLayer !== undefined) {
        const layer = layers[selectedLayer];
        selectedLayerName =
          (layer as any)?.layer?.label ??
          (layer as any)?.layer?.name ??
          String(selectedLayer);
      }

      let overviewWithJordbundImage: Uint8Array | undefined;
      let histogramImage: Uint8Array | undefined;
      let subjectPolygonImage: Uint8Array | undefined;
      let allPointsMapImage: Uint8Array | undefined;
      let heatmapImage: Uint8Array | undefined;
      let statisticsCardImage: Uint8Array | undefined;
      let polygonAnalyzerImage: Uint8Array | undefined;
      // Build a documentID → 1-based row index map that matches the Bilag
      // transactions table order in `JPBRepportGen.buildTransactionsTable`
      // (which itself filters `referenceProperties` by `checkedDocumentIds`).
      // Sharing the same source-of-truth keeps the row numbers in the table
      // and the labels on the jordbund map aligned.
      const reportRowIndices = new Map<string, number>();
      referenceProperties
        .filter((p) => checkedItems.has(p.documentID))
        .forEach((p, i) => {
          reportRowIndices.set(p.documentID, i + 1);
        });

      // ===================================================================
      // PHASE A — Synchronous snapshot of all live-DOM-dependent inputs.
      //
      // Everything below this point only depends on data + offscreen
      // clones, so the user can close / minimize / collapse the JPB window
      // freely once Phase A returns. This is the entire point of the
      // off-screen capture pipeline: the live React tree no longer needs
      // to remain visible for the rest of report generation to succeed.
      // ===================================================================
      const stage = JPBCaptureStage.create();
      let statisticsClone: HTMLElement | undefined;
      let polygonAnalyzerClone: HTMLElement | undefined;
      let serializedHistogram: ReturnType<typeof serializeHistogramSvg>;

      // Briefly expand collapsed sections on the LIVE DOM so the React tree
      // re-renders the extra rows; the resulting DOM is then cloned into the
      // stage and the live state is immediately restored. The user sees at
      // most a one-frame flicker — and crucially, after the clone is
      // attached to the stage, none of the subsequent capture work depends
      // on the live elements anymore.
      setForceStatisticsSoilExpanded(true);
      const liveDetailsToRestore: HTMLDetailsElement[] = [];
      if (polygonAnalyzerRef.current) {
        polygonAnalyzerRef.current.querySelectorAll("details").forEach((d) => {
          const el = d as HTMLDetailsElement;
          if (!el.open) {
            el.open = true;
            liveDetailsToRestore.push(el);
          }
        });
      }
      try {
        // Two animation frames so React has flushed the expansion and the
        // browser has performed layout against the new DOM.
        await new Promise<void>((resolve) =>
          requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
        );

        if (statisticsRef.current) {
          statisticsClone = stage.cloneInto(statisticsRef.current);
        }
        if (polygonAnalyzerRef.current) {
          polygonAnalyzerClone = stage.cloneInto(polygonAnalyzerRef.current, {
            expandDetails: true,
          });
        }
        if (filteredDataForHistogram.length > 0 && histogramRef.current) {
          serializedHistogram = serializeHistogramSvg(histogramRef.current);
        }
      } finally {
        // Restore live DOM — Phase B no longer needs it.
        setForceStatisticsSoilExpanded(false);
        liveDetailsToRestore.forEach((d) => {
          d.open = false;
        });
      }

      // Snapshot the map style reference too (Phase B uses it to spin up
      // detached MapLibre instances; the live map can change freely after).
      const capturedMapStyle = mapInfoRef.current?.mapStyle;
      const capturedCheckedIds = Array.from(checkedItems);

      // ===================================================================
      // PHASE B — Asynchronous rendering against the captured snapshots.
      //
      // Runs all four off-screen maps + both cloned cards + the histogram
      // in parallel. Each capture is wrapped in its own try/catch so a
      // single failure does not prevent the rest of the report from being
      // assembled.
      // ===================================================================
      try {
        const captureResults = await Promise.allSettled([
          // Map captures — only need data + mapStyle.
          (async () => {
            if (!referenceProperties.length || !capturedMapStyle)
              return undefined;
            if (!polygonAnalysisForReport?.polygonGeoJson) return undefined;
            return await renderJPBSubjectPolygonImage({
              mapStyle: capturedMapStyle,
              polygonGeoJson: polygonAnalysisForReport.polygonGeoJson,
            });
          })(),
          (async () => {
            if (!referenceProperties.length || !capturedMapStyle)
              return undefined;
            return await renderJPBAllPointsMapImage({
              mapStyle: capturedMapStyle,
              referenceProperties,
              checkedDocumentIds: capturedCheckedIds,
              bufferGeometry,
            });
          })(),
          (async () => {
            if (!referenceProperties.length || !capturedMapStyle)
              return undefined;
            return await renderJPBHeatmapImage({
              mapStyle: capturedMapStyle,
              referenceProperties,
              checkedDocumentIds: capturedCheckedIds,
              bufferGeometry,
            });
          })(),
          (async () => {
            if (!referenceProperties.length || !capturedMapStyle)
              return undefined;
            return await renderJPBOverviewWithJordbundImage({
              mapStyle: capturedMapStyle,
              referenceProperties,
              checkedDocumentIds: capturedCheckedIds,
              bufferGeometry,
              reportRowIndices,
            });
          })(),
          // DOM clones — let fonts settle once for the whole stage, then
          // run both card snapshots in parallel.
          (async () => {
            if (!statisticsClone && !polygonAnalyzerClone) {
              return [undefined, undefined] as [
                Uint8Array | undefined,
                Uint8Array | undefined,
              ];
            }
            await stage.waitReady();
            const [stats, analyzer] = await Promise.all([
              statisticsClone
                ? renderClonedElementImage(statisticsClone)
                : Promise.resolve(undefined),
              polygonAnalyzerClone
                ? renderClonedElementImage(polygonAnalyzerClone)
                : Promise.resolve(undefined),
            ]);
            return [stats, analyzer] as [
              Uint8Array | undefined,
              Uint8Array | undefined,
            ];
          })(),
          // Histogram from the serialized SVG snapshot.
          (async () => {
            if (!serializedHistogram) return undefined;
            return await renderHistogramFromSerialized(serializedHistogram);
          })(),
        ]);

        const unwrap = <T,>(
          r: PromiseSettledResult<T>,
          label: string,
        ): T | undefined => {
          if (r.status === "fulfilled") return r.value;
          console.warn(`JPB capture failed: ${label}`, r.reason);
          return undefined;
        };

        subjectPolygonImage = unwrap(
          captureResults[0] as PromiseSettledResult<Uint8Array | undefined>,
          "subjectPolygon",
        );
        allPointsMapImage = unwrap(
          captureResults[1] as PromiseSettledResult<Uint8Array | undefined>,
          "allPointsMap",
        );
        heatmapImage = unwrap(
          captureResults[2] as PromiseSettledResult<Uint8Array | undefined>,
          "heatmap",
        );
        overviewWithJordbundImage = unwrap(
          captureResults[3] as PromiseSettledResult<Uint8Array | undefined>,
          "overviewWithJordbund",
        );
        const cardsResult = unwrap(
          captureResults[4] as PromiseSettledResult<
            [Uint8Array | undefined, Uint8Array | undefined]
          >,
          "domClones",
        );
        statisticsCardImage = cardsResult?.[0];
        polygonAnalyzerImage = cardsResult?.[1];
        histogramImage = unwrap(
          captureResults[5] as PromiseSettledResult<Uint8Array | undefined>,
          "histogram",
        );
      } catch (e) {
        console.error("Failed to capture JPB images", e);
      } finally {
        stage.destroy();
      }

      const userEmail = (sessionState as any)?.userName as
        | string
        | undefined;
      const subjectAreaM2: number | undefined =
        polygonAnalysisForReport?.polygonAreaM2;

      const {
        subjectAddress,
        subjectKommune,
        subjectBfe,
        subjectMatr,
      } = await resolveJpbReportSubjectInfo({
        selectedLayer:
          selectedLayer !== undefined ? layers[selectedLayer] : undefined,
        analyzerLayer:
          analyzerStateRef.current.selectedLayerId !== undefined
            ? layers[analyzerStateRef.current.selectedLayerId]
            : undefined,
        polygonGeoJson: polygonAnalysisForReport?.polygonGeoJson,
        selectedLayerName,
        resolveAddressWithRetry: resolvePropertyInfoStyleAddressWithRetry,
      });

      const searchRadiusKm = SettingsManager.getSystemSetting(
        "jpb.defaultRadiusKm",
        10,
      ) as number;
      const liveBufferDistanceKm = bufferDistanceKm ?? undefined;
      const sourceLayerInfo =
        selectedLayer !== undefined ? layers[selectedLayer] : undefined;
      const sourceLayerName =
        sourceLayerInfo?.datasetname ?? selectedLayerName ?? "-";
      const sourceLayerGeometryKind =
        ((): JPBReportData["sourceLayerGeometryKind"] => {
          const fc = sourceLayerInfo?.geoJson;
          const features: any[] = Array.isArray(fc?.features)
            ? fc.features
            : [];
          if (features.length === 0) return undefined;
          const types = new Set<string>(
            features
              .map((f) => f?.geometry?.type as string | undefined)
              .filter((t): t is string => typeof t === "string"),
          );
          if (types.size === 0) return undefined;
          if (types.size > 1) return "mixed";
          const t = Array.from(types)[0];
          switch (t) {
            case "Point":
              return "point";
            case "MultiPoint":
              return "multipoint";
            case "LineString":
            case "MultiLineString":
              return "line";
            case "Polygon":
              return "polygon";
            case "MultiPolygon":
              return "multipolygon";
            default:
              return "mixed";
          }
        })();
      const sourceFeatureCount = Array.isArray(
        sourceLayerInfo?.geoJson?.features,
      )
        ? sourceLayerInfo!.geoJson.features.length
        : undefined;
      const searchAreaKm2 = bufferGeometry
        ? (() => {
          try {
            const m2 = lengthByUserPreference(bufferGeometry as any);
            return Number.isFinite(m2) ? m2 / 1_000_000 : undefined;
          } catch (e) {
            console.warn("Failed to compute search area km²", e);
            return undefined;
          }
        })()
        : undefined;

      const applyIndexCalculation =
        priceIndexSource !== JPBPriceIndexSource.None;
      const priceIndexSourceLabel =
        priceIndexSource === JPBPriceIndexSource.Molbak
          ? "Mølbaks Jordprisindeks"
          : priceIndexSource === JPBPriceIndexSource.AgroCura
            ? "AgroCuras Jordprisindeks"
            : undefined;

      const reportData: JPBReportData = {
        yearGrouping: appliedYearGrouping,
        averageCalculationMethod,
        assumptions,
        referenceProperties,
        checkedDocumentIds: Array.from(checkedItems),
        calculationDateFrom: appliedCalculationDateTo
          ? withYearsOffset(appliedCalculationDateTo, -appliedYearGrouping)
          : undefined,
        calculationDateTo: appliedCalculationDateTo ?? undefined,
        overviewWithJordbundImage,
        histogramImage,
        userEmail,
        reportDate: new Date(),
        searchRadiusKm: liveBufferDistanceKm ?? searchRadiusKm,
        bufferDistanceKm: liveBufferDistanceKm,
        searchAreaKm2,
        sourceLayerName,
        sourceLayerGeometryKind,
        sourceFeatureCount,
        applyIndexCalculation,
        priceIndexSourceLabel,
        polygonAnalysis: polygonAnalysisForReport
          ? {
            ...polygonAnalysisForReport,
            subjectAddress:
              subjectAddress ?? polygonAnalysisForReport.subjectAddress,
          }
          : undefined,
        subjectPolygonImage,
        allPointsMapImage,
        heatmapImage,
        statisticsCardImage,
        polygonAnalyzerImage,
        subjectKommune,
        subjectBfe,
        subjectMatr,
        subjectAreaM2,
      };

      await handleGenerateJPBRapport(reportData);
    } catch (e) {
      console.error("Failed to generate JPB report", e);
      message.error(
        Localization.getFormattedText("Kunne ikke danne rapport. {error}", {
          error: e instanceof Error ? e.message : String(e),
        }),
      );
    } finally {
      patchResults({ isGeneratingReport: false });
    }
  };

  // MARK: Computed
  const selectableLayers = useMemo(() => {
    return Object.values(layers).filter(
      (layer) =>
        layer.layerVariability !== LayerVariabilityType.FixedFeatureLayer,
    );
  }, [layers]);

  const simpleModeLayerGeoKey = useMemo(() => {
    if (selectedLayer === undefined) return "";
    try {
      return JSON.stringify(layers[selectedLayer]?.geoJson);
    } catch {
      return String(selectedLayer);
    }
  }, [selectedLayer, layers]);

  useEffect(() => {
    if (jpbViewMode !== "simple") {
      return;
    }
    const map = mapFacade?.getMapPop();
    if (!map) {
      return;
    }
    addSearchAreaOutline(map, "jpb-search-area", "#d6c251");
    const defaultRadiusKm =
      (SettingsManager.getSystemSetting("jpb") &&
        (SettingsManager.getSystemSetting("jpb") as any).defaultRadiusKm) ||
      10;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const apply = () => {
      if (selectedLayer === undefined) {
        setSearchAreaGeometry(map, "jpb-search-area", null);
        if (debounceTimer) {
          clearTimeout(debounceTimer);
          debounceTimer = null;
        }
        patchSearch({ bufferGeometry: null });
        return;
      }
      const layerInfo = layers[selectedLayer];
      const geom = layerInfo?.geoJson;
      if (!geom) {
        setSearchAreaGeometry(map, "jpb-search-area", null);
        if (debounceTimer) {
          clearTimeout(debounceTimer);
          debounceTimer = null;
        }
        patchSearch({ bufferGeometry: null });
        return;
      }
      const bufKm =
        layerInfo && MapitUtils.isPointLayer(layerInfo.type)
          ? defaultRadiusKm
          : 0;
      const bufferedGeom = buffer(geom, bufKm, { units: "kilometers" });
      const combinedBufferGeom =
        JPBManager.toCombinedSearchGeometry(bufferedGeom) || bufferedGeom;
      setSearchAreaGeometry(map, "jpb-search-area", combinedBufferGeom);
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(() => {
        patchSearch({ bufferGeometry: combinedBufferGeom });
        debounceTimer = null;
      }, 500);
    };

    apply();

    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      removeSearchAreaLayer(map, "jpb-search-area");
    };
  }, [jpbViewMode, selectedLayer, simpleModeLayerGeoKey, mapFacade]);

  const availableYearGroupings = useMemo<YearGrouping[]>(() => {
    const allYears: YearGrouping[] = [
      YearGrouping.OneYear,
      YearGrouping.TwoYears,
      YearGrouping.ThreeYears,
      YearGrouping.FourYears,
      YearGrouping.FiveYears,
    ];
    if (!dateBounds || !appliedCalculationDateTo) {
      return allYears;
    }
    const minDateMs = toStartOfDay(dateBounds.minDate).getTime();
    const valid = allYears.filter((years) => {
      const fromDate = withYearsOffset(appliedCalculationDateTo, -years);
      return toStartOfDay(fromDate).getTime() >= minDateMs;
    });
    return valid.length > 0 ? valid : [YearGrouping.OneYear];
  }, [dateBounds, appliedCalculationDateTo]);

  const minCalculationDateTo = useMemo(() => {
    if (!dateBounds) return null;
    return toStartOfDay(
      withYearsOffset(dateBounds.minDate, appliedYearGrouping),
    );
  }, [dateBounds, appliedYearGrouping]);

  useEffect(() => {
    if (!availableYearGroupings.includes(appliedYearGrouping)) {
      patchFilters({
        appliedYearGrouping:
          availableYearGroupings[availableYearGroupings.length - 1],
      });
    }
  }, [availableYearGroupings, appliedYearGrouping]);

  const filteredDataForHistogram = useMemo(() => {
    if (referenceProperties.length === 0) {
      return [];
    }
    return referenceProperties.filter((p) => checkedItems.has(p.documentID));
  }, [referenceProperties, checkedItems]);

  const tableData = useMemo(() => referenceProperties, [referenceProperties]);

  const activeMolbakIndexLandsdele = useMemo(() => {
    const landsdele = new Set<string>();
    referenceProperties.forEach((property) => {
      if (!checkedItems.has(property.documentID) || !property.kommuneCode) {
        return;
      }
      landsdele.add(
        JPBManager.getMolbakDisplayLandsdelForKommune(property.kommuneCode),
      );
    });
    return [...landsdele].sort((a, b) => a.localeCompare(b, "da"));
  }, [checkedItems, referenceProperties]);

  const tableDocumentIds = useMemo(() => {
    return new Set(tableData.map((property) => property.documentID));
  }, [tableData]);

  // MARK: Data Fetch
  useEffect(() => {
    if (!mapFacade) return;
    const abortController = new AbortController();

    async function fetchData() {
      if (!bufferGeometry) {
        patchResults({
          lastFetchKey: null,
          referenceProperties: [],
          loading: false,
        });
        patchFilters({ checkedItems: new Set() });
        return;
      }

      patchResults({ loading: true, error: null });
      try {
        const polygonCoordinates =
          JPBManager.extractPolygonCoordinates(bufferGeometry);

        if (!polygonCoordinates) {
          patchResults({
            error: Localization.getText(
              "Could not extract polygon coordinates from buffer",
            ),
            loading: false,
          });
          return;
        }

        const bounds = await JPBManager.fetchDateBounds(true);
        if (abortController.signal.aborted) return;

        if (!bounds?.maxDate || !bounds?.minDate) {
          patchResults({
            error: Localization.getText(
              "Could not read latest transaction date from index",
            ),
            loading: false,
          });
          return;
        }

        patchResults({
          dateBounds: {
            minDate: toStartOfDay(bounds.minDate),
            maxDate: toStartOfDay(bounds.maxDate),
          },
        });

        const resolvedCalculationDateTo =
          appliedCalculationDateTo ?? toStartOfDay(bounds.maxDate);

        const fetchKey = `${appliedYearGrouping}|${priceIndexSource}|${resolvedCalculationDateTo?.getTime() ?? 0}|${stableStringify(polygonCoordinates)}`;
        if (lastJpbDataFetchKey === fetchKey) {
          patchResults({ loading: false });
          return;
        }

        const properties = await JPBManager.fetchReferenceProperties(
          {
            polygonCoordinates,
            yearGrouping: appliedYearGrouping,
            calculationDateTo: resolvedCalculationDateTo ?? undefined,
            priceIndexSource,
            logFetchSummary: hasAccessToFeature(Feature.Debugging),
          },
          abortController.signal,
        );
        if (abortController.signal.aborted) return;

        patchResults({
          lastFetchKey: fetchKey,
          referenceProperties: properties,
        });

        if (!appliedCalculationDateTo) {
          patchFilters({
            appliedCalculationDateTo: toStartOfDay(bounds.maxDate),
          });
        }

        const systemAssumptions = SettingsManager.getSystemSetting(
          "jpb.assumptions",
          DEFAULT_ASSUMPTIONS,
        );
        patchFilters({
          systemPriceAssumptions: {
            pricePerMeterMin: systemAssumptions.pricePerMeterMin,
            pricePerMeterMax: systemAssumptions.pricePerMeterMax,
            areaMin: systemAssumptions.areaMin,
            areaMax: systemAssumptions.areaMax,
            effectiveMarkOverlapThreshold:
              systemAssumptions.effectiveMarkOverlapThreshold,
          },
        });

        if (customPriceAssumptions === undefined) {
          patchFilters({
            customPriceAssumptions: {
              pricePerMeterMin: systemAssumptions.pricePerMeterMin,
              pricePerMeterMax: systemAssumptions.pricePerMeterMax,
            },
          });
        }

        const { checkedItems: initialCheckedItems } =
          getCheckedItemsFromCurrentAssumptions(
            properties,
            customPriceAssumptions,
            priceMode,
          );
        const sanitizedInitialCheckedItems = new Set(initialCheckedItems);
        properties.forEach((prop) => {
          if (
            JPBManager.getSelectionLockState(prop, customPriceAssumptions)
              .isLocked
          ) {
            sanitizedInitialCheckedItems.delete(prop.documentID);
          }
        });
        patchFilters({ checkedItems: sanitizedInitialCheckedItems });
      } catch (err: any) {
        if (abortController.signal.aborted) return;
        console.error("Got error searching: ", err);
        patchResults({ error: err.message || String(err) });
      } finally {
        if (!abortController.signal.aborted) {
          patchResults({ loading: false });
        }
      }
    }

    fetchData();

    return () => {
      abortController.abort();
    };
  }, [
    bufferGeometry,
    appliedYearGrouping,
    appliedCalculationDateTo,
    priceIndexSource,
  ]);

  // Update checked items when assumptions or calculation settings change
  useEffect(() => {
    if (referenceProperties.length === 0) return;
    const { checkedItems: newCheckedItems } =
      getCheckedItemsFromCurrentAssumptions(
        referenceProperties,
        customPriceAssumptions,
        priceMode,
      );
    updateCheckedItems(newCheckedItems);
  }, [
    appliedYearGrouping,
    referenceProperties,
    customPriceAssumptions,
    priceMode,
    settingsRefreshToken,
  ]);

  // Reset hideUnselected when areaUnit or priceMode changes
  useEffect(() => {
    patchFilters({ hideUnselected: "none" });
  }, [areaUnit, priceMode]);

  // MARK: Price Handlers
  const handlePriceRangeChange = useCallback(
    (min: number, max: number) => {
      const newAssumptions = {
        ...customPriceAssumptions,
        pricePerMeterMin: min,
        pricePerMeterMax: max,
      };

      patchFilters({
        customPriceAssumptions: {
          ...newAssumptions,
        },
      });

      const { checkedItems: newCheckedItems } =
        getCheckedItemsFromCurrentAssumptions(
          referenceProperties,
          newAssumptions,
          priceMode,
        );
      updateCheckedItems(newCheckedItems);
    },
    [
      patchFilters,
      customPriceAssumptions,
      referenceProperties,
      appliedYearGrouping,
      priceMode,
      updateCheckedItems,
    ],
  );

  const handleBufferGeometryChange = useCallback(
    (geometry: any) => {
      patchSearch({ bufferGeometry: geometry });
    },
    [patchSearch],
  );

  const handleSelectedLayerChange = useCallback(
    (layerId: number | undefined) => {
      patchSearch({ selectedLayer: layerId });
    },
    [patchSearch],
  );

  const handleBufferDistanceChange = useCallback(
    (distanceKm: number, layerId: number | undefined) => {
      patchSearch({ bufferDistanceKm: distanceKm });
      if (layerId === undefined) {
        return;
      }
      patchSearch((prev) => {
        const key = String(layerId);
        if (prev.bufferDistanceByLayer[key] === distanceKm) {
          return {};
        }
        return {
          bufferDistanceByLayer: {
            ...prev.bufferDistanceByLayer,
            [key]: distanceKm,
          },
        };
      });
    },
    [patchSearch],
  );

  const handleAreaUnitChange = useCallback(
    (unit: AreaUnit) => {
      patchFilters({ areaUnit: unit });
      persistJPBUserSetting("jpb.areaUnit", unit);
    },
    [patchFilters, persistJPBUserSetting],
  );

  const handlePriceModeChange = useCallback(
    (mode: PriceMode) => {
      patchFilters({ priceMode: mode });
      persistJPBUserSetting("jpb.priceMode", mode);
    },
    [patchFilters, persistJPBUserSetting],
  );

  const handleAverageCalculationMethodChange = useCallback(
    (method: AverageCalculationMethod) => {
      patchFilters({ averageCalculationMethod: method });
      persistJPBUserSetting("jpb.averageCalculationMethod", method);
    },
    [patchFilters, persistJPBUserSetting],
  );

  const handlePriceIndexSourceChange = useCallback(
    (source: JPBPriceIndexSource) => {
      const effectiveSource = normalizeAccessiblePriceIndexSource(source);
      patchFilters({ priceIndexSource: effectiveSource });
      persistJPBUserSetting("jpb.priceIndexSource", effectiveSource);
    },
    [patchFilters, normalizeAccessiblePriceIndexSource, persistJPBUserSetting],
  );

  const handlePersistUserSettingsChange = useCallback(
    (persist: boolean) => {
      persistUserSettingsRef.current = persist;
      setPersistUserSettings(persist);
      if (persist) {
        localStorage.setItem(JPB_PERSIST_USER_SETTINGS_KEY, "true");
        persistJPBUserSetting("jpb.areaUnit", areaUnit);
        persistJPBUserSetting("jpb.priceMode", priceMode);
        persistJPBUserSetting(
          "jpb.averageCalculationMethod",
          averageCalculationMethod,
        );
        persistJPBUserSetting("jpb.priceIndexSource", priceIndexSource);
        persistJPBUserSetting("jpb.viewMode", jpbViewMode);
        persistJPBUserSetting(
          JPB_PERSISTED_YEAR_GROUPING_KEY,
          String(appliedYearGrouping),
        );
      } else {
        localStorage.setItem(JPB_PERSIST_USER_SETTINGS_KEY, "false");
      }
    },
    [
      areaUnit,
      appliedYearGrouping,
      averageCalculationMethod,
      jpbViewMode,
      persistJPBUserSetting,
      priceIndexSource,
      priceMode,
    ],
  );

  const handleCustomAssumptionsChange = useCallback(
    (newAssumptions: Partial<JPBAssumptions>) => {
      patchFilters((prev) => ({
        customPriceAssumptions: {
          ...prev.customPriceAssumptions,
          ...newAssumptions,
        },
      }));
    },
    [patchFilters],
  );

  const handleResetToSystemAssumptions = useCallback(() => {
    if (!systemPriceAssumptions) return;

    patchFilters({
      customPriceAssumptions: {
        pricePerMeterMin: systemPriceAssumptions.pricePerMeterMin,
        pricePerMeterMax: systemPriceAssumptions.pricePerMeterMax,
        excludeBuildings: false,
      },
    });

    const { checkedItems: newCheckedItems } =
      getCheckedItemsFromCurrentAssumptions(
        referenceProperties,
        {
          pricePerMeterMin: systemPriceAssumptions.pricePerMeterMin,
          pricePerMeterMax: systemPriceAssumptions.pricePerMeterMax,
          excludeBuildings: false,
        },
        priceMode,
      );
    updateCheckedItems(newCheckedItems);
  }, [
    systemPriceAssumptions,
    patchFilters,
    referenceProperties,
    appliedYearGrouping,
    priceMode,
    updateCheckedItems,
  ]);

  const handleMinimizedChange = useCallback(
    (isMinimized: boolean) => {
      patchUi({ isWindowMinimized: isMinimized });
    },
    [patchUi],
  );

  const handleHideUnselectedChange = useCallback(
    (mode: typeof hideUnselected) => {
      patchFilters({ hideUnselected: mode });
    },
    [patchFilters],
  );

  // MARK: Map Layers
  useEffect(() => {
    if (!mapFacade || referenceProperties.length === 0) {
      removeJPBMapLayers(mapFacade?.getMapPop());
      return;
    }

    const map = mapFacade.getMapPop();
    if (!map) return;

    const cleanup = setupJPBMapLayers(
      map,
      referenceProperties,
      checkedItemsRef.current,
      priceMode,
      areaUnit,
      {
        onFeatureClick: (documentID: string, ctrlKey: boolean) => {
          if (!ctrlKey) {
            if (tableRef.current && tableDocumentIds.has(documentID)) {
              // The table lives at the bottom of the modal; make sure the modal body scrolls down to it first.
              tableScrollAnchorRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
              tableRef.current.scrollToRow(documentID);
            }
          } else {
            if (lockedDocumentIds.has(documentID)) {
              return;
            }
            const featureState = map.getFeatureState({
              source: SOURCE_ID,
              id: documentID,
            });
            const currentChecked = featureState?.checked === true;
            const newChecked = !currentChecked;

            const newCheckedItems = new Set(checkedItemsRef.current);
            if (newChecked) {
              newCheckedItems.add(documentID);
            } else {
              newCheckedItems.delete(documentID);
            }

            updateCheckedItems(newCheckedItems);
          }
        },
      },
      lockedDocumentIds,
      customPriceAssumptions,
    );

    return cleanup;
  }, [
    mapFacade,
    referenceProperties,
    priceMode,
    areaUnit,
    lockedDocumentIds,
    mapRefreshToken,
    tableDocumentIds,
    customPriceAssumptions,
  ]);

  // Hover label copy + building icon patch + feature-state (checked) without rebuilding main point GeoJSON
  useEffect(() => {
    if (!mapFacade || referenceProperties.length === 0) return;
    const map = mapFacade.getMapPop();
    if (!map || !map.getSource(SOURCE_ID)) return;

    updateJPBHoverLabelsData(map, referenceProperties, {
      checkedItems,
      assumptions: customPriceAssumptions,
      priceMode,
      areaUnit,
    });
    updateFeatureStates(map, referenceProperties, checkedItems);
  }, [
    mapFacade,
    checkedItems,
    referenceProperties,
    customPriceAssumptions,
    priceMode,
    areaUnit,
  ]);

  useEffect(() => {
    if (!mapFacade || referenceProperties.length === 0) return;
    const map = mapFacade.getMapPop();
    if (!map) return;

    if (map.getLayer(LAYER_ID)) {
      map.setPaintProperty(
        LAYER_ID,
        "circle-stroke-opacity",
        hideUnselected === "unchecked"
          ? ["case", ["to-boolean", ["feature-state", "checked"]], 1, 0]
          : hideUnselected === "locked"
            ? [
              "case",
              [
                "in",
                ["get", "documentID"],
                ["literal", Array.from(lockedDocumentIds)],
              ],
              0,
              1,
            ]
            : 1,
      );
      map.setPaintProperty(
        LAYER_ID,
        "circle-opacity",
        hideUnselected === "unchecked"
          ? ["case", ["to-boolean", ["feature-state", "checked"]], 1, 0]
          : hideUnselected === "locked"
            ? [
              "case",
              [
                "in",
                ["get", "documentID"],
                ["literal", Array.from(lockedDocumentIds)],
              ],
              0,
              1,
            ]
            : 1,
      );
    }

    if (map.getLayer(LAYER_ID_POLY)) {
      map.setPaintProperty(
        LAYER_ID_POLY,
        "fill-opacity",
        hideUnselected === "unchecked"
          ? [
            "interpolate",
            ["linear"],
            ["zoom"],
            13,
            ["case", ["to-boolean", ["feature-state", "checked"]], 0, 0],
            14,
            ["case", ["to-boolean", ["feature-state", "checked"]], 0.5, 0],
          ]
          : ["interpolate", ["linear"], ["zoom"], 13, 0, 14, 0.5],
      );
    }

    if (map.getLayer(LAYER_ID_POLY + "-outline")) {
      map.setPaintProperty(
        LAYER_ID_POLY + "-outline",
        "line-opacity",
        hideUnselected === "unchecked"
          ? [
            "interpolate",
            ["linear"],
            ["zoom"],
            12.5,
            ["case", ["to-boolean", ["feature-state", "checked"]], 0, 0],
            13.5,
            ["case", ["to-boolean", ["feature-state", "checked"]], 1, 0],
          ]
          : ["interpolate", ["linear"], ["zoom"], 12.5, 0, 13.5, 1],
      );
    }

    if (map.getLayer(LAYER_ID_FARMLAND)) {
      map.setPaintProperty(
        LAYER_ID_FARMLAND,
        "fill-opacity",
        hideUnselected === "unchecked"
          ? [
            "interpolate",
            ["linear"],
            ["zoom"],
            13,
            ["case", ["to-boolean", ["feature-state", "checked"]], 0, 0],
            14,
            ["case", ["to-boolean", ["feature-state", "checked"]], 0.6, 0],
          ]
          : ["interpolate", ["linear"], ["zoom"], 13, 0, 14, 0.6],
      );
    }

    if (map.getLayer(LAYER_ID_FARMLAND + "-outline")) {
      map.setPaintProperty(
        LAYER_ID_FARMLAND + "-outline",
        "line-opacity",
        hideUnselected === "unchecked"
          ? [
            "interpolate",
            ["linear"],
            ["zoom"],
            12.5,
            ["case", ["to-boolean", ["feature-state", "checked"]], 0, 0],
            13.5,
            ["case", ["to-boolean", ["feature-state", "checked"]], 0.8, 0],
          ]
          : ["interpolate", ["linear"], ["zoom"], 12.5, 0, 13.5, 0.8],
      );
    }

    if (
      map.getLayer(LAYER_ID_JORDBUNDSKORT) &&
      map.getSource(SOURCE_ID_JORDBUNDSKORT)
    ) {
      const jordbundskortSource = map.getSource(SOURCE_ID_JORDBUNDSKORT) as any;
      if (jordbundskortSource && jordbundskortSource.type === "geojson") {
        const updatedJordbundskortGeoJson = createJordbundskortGeoJson(
          referenceProperties,
          checkedItems,
          lockedDocumentIds,
        );
        jordbundskortSource.setData(updatedJordbundskortGeoJson);
      }
      map.setPaintProperty(
        LAYER_ID_JORDBUNDSKORT,
        "fill-opacity",
        hideUnselected === "unchecked"
          ? [
            "interpolate",
            ["linear"],
            ["zoom"],
            13,
            ["case", ["to-boolean", ["get", "checked"]], 0, 0],
            14,
            ["case", ["to-boolean", ["get", "checked"]], 0.4, 0],
          ]
          : hideUnselected === "locked"
            ? [
              "interpolate",
              ["linear"],
              ["zoom"],
              13,
              ["case", ["to-boolean", ["get", "locked"]], 0, 0],
              14,
              ["case", ["to-boolean", ["get", "locked"]], 0, 0.4],
            ]
            : ["interpolate", ["linear"], ["zoom"], 13, 0, 14, 0.4],
      );
    }

    const checkedIds = Array.from(checkedItems);
    const lockedIds = Array.from(lockedDocumentIds);

    const showOnlyCheckedFilter: any =
      checkedIds.length > 0
        ? ["in", ["get", "documentID"], ["literal", checkedIds]]
        : ["==", ["get", "documentID"], "__none__"];

    const hideLockedFilter: any =
      lockedIds.length > 0
        ? ["!", ["in", ["get", "documentID"], ["literal", lockedIds]]]
        : null;

    const applyFilter = (layerId: string, baseFilter: any = null) => {
      if (!map.getLayer(layerId)) return;
      if (hideUnselected === "none") {
        map.setFilter(layerId, baseFilter);
        return;
      }

      let activeFilter: any = null;
      if (hideUnselected === "unchecked") {
        activeFilter = showOnlyCheckedFilter;
      } else if (hideUnselected === "locked") {
        activeFilter = hideLockedFilter;
      }

      if (activeFilter) {
        map.setFilter(
          layerId,
          baseFilter ? ["all", baseFilter, activeFilter] : activeFilter,
        );
      } else {
        map.setFilter(layerId, baseFilter);
      }
    };

    applyFilter(LAYER_ID);
    // Issue symbol layer should follow hide/show behavior like other point overlays.
    applyFilter(LAYER_ID_ISSUE, [
      "all",
      ["==", ["get", "hasIssue"], true],
      ["==", ["get", "showPipelineIssueMarker"], true],
    ]);
    // Building layer needs to preserve its hasBuilding filter
    applyFilter(LAYER_ID_BUILDING, [
      "all",
      ["==", ["get", "hasBuilding"], true],
      ["!=", ["get", "hasIssue"], true],
    ]);
    applyFilter(LAYER_ID_POLY);
    applyFilter(LAYER_ID_POLY + "-outline");
    applyFilter(LAYER_ID_FARMLAND);
    applyFilter(LAYER_ID_FARMLAND + "-outline");
    // Jordbundskort layer should not be filtered - it uses paint properties for visibility
  }, [
    mapFacade,
    hideUnselected,
    referenceProperties.length,
    checkedItems,
    lockedDocumentIds,
    mapRefreshToken,
  ]);

  // MARK: Mask Layer
  useEffect(() => {
    if (!mapFacade) return;

    const map = mapFacade.getMapPop();
    if (!map) return;

    if (!map.getSource(MASK_SOURCE_ID)) {
      map.addSource(MASK_SOURCE_ID, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });
    }

    if (!map.getLayer(MASK_LAYER_ID)) {
      const maskLayerSpec: any = {
        id: MASK_LAYER_ID,
        type: "fill",
        source: MASK_SOURCE_ID,
        paint: {
          "fill-color": "#000000",
          "fill-opacity": 0.15,
        },
      };
      if (map.getLayer("jpb-search-area-outline")) {
        map.addLayer(maskLayerSpec, "jpb-search-area-outline");
      } else {
        map.addLayer(maskLayerSpec);
      }
    }

    const updateMask = () => {
      if (!bufferGeometry) {
        const source = map.getSource(MASK_SOURCE_ID) as GeoJSONSource;
        if (source) {
          source.setData({
            type: "FeatureCollection",
            features: [],
          } as any);
        }
        return;
      }

      try {
        const worldBbox: [number, number, number, number] = [
          -180, -90, 180, 90,
        ];
        const worldPolygon = bboxPolygon(worldBbox);
        const bufferFeature =
          JPBManager.toCombinedSearchGeometry(bufferGeometry);
        if (!bufferFeature) {
          const source = map.getSource(MASK_SOURCE_ID) as GeoJSONSource;
          if (source) {
            source.setData({
              type: "FeatureCollection",
              features: [],
            } as any);
          }
          return;
        }

        const maskGeometry = difference(
          featureCollection([worldPolygon, bufferFeature]),
        );

        if (maskGeometry) {
          const maskFeatureCollection = {
            type: "FeatureCollection",
            features: [maskGeometry],
          };
          const source = map.getSource(MASK_SOURCE_ID) as GeoJSONSource;
          if (source) {
            source.setData(maskFeatureCollection as any);
          }
        } else {
          const source = map.getSource(MASK_SOURCE_ID) as GeoJSONSource;
          if (source) {
            source.setData({
              type: "FeatureCollection",
              features: [],
            } as any);
          }
        }
      } catch (error) {
        console.error("Error creating mask layer:", error);
        const source = map.getSource(MASK_SOURCE_ID) as GeoJSONSource;
        if (source) {
          source.setData({
            type: "FeatureCollection",
            features: [],
          } as any);
        }
      }
    };

    updateMask();

    return () => {
      if (map.getLayer(MASK_LAYER_ID)) map.removeLayer(MASK_LAYER_ID);
      if (map.getSource(MASK_SOURCE_ID)) map.removeSource(MASK_SOURCE_ID);
    };
  }, [mapFacade, bufferGeometry]);

  // MARK: Pin Color Change
  useEffect(() => {
    if (!mapFacade || selectedLayer === undefined) {
      setJpbBufferPointLegendLayerId(undefined);
      const map = mapFacade?.getMapPop();
      if (map) {
        Object.keys(originalIconImagesRef.current).forEach((layerId) => {
          const originalIconImage = originalIconImagesRef.current[layerId];
          if (originalIconImage !== undefined && map.getLayer(layerId)) {
            map.setLayoutProperty(layerId, "icon-image", originalIconImage);
          }
        });
        originalIconImagesRef.current = {};
      }
      return;
    }

    const layerInfo = layers[selectedLayer];
    if (!layerInfo || !layerInfo.handle) {
      setJpbBufferPointLegendLayerId(undefined);
      return;
    }

    const isPointLayer =
      layerInfo.type === LayerType.GeoJSON_Point ||
      layerInfo.type === LayerType.GeoJSON_PointRadius;

    if (!isPointLayer) {
      setJpbBufferPointLegendLayerId(undefined);
      const map = mapFacade?.getMapPop();
      if (map) {
        Object.keys(originalIconImagesRef.current).forEach((layerId) => {
          const originalIconImage = originalIconImagesRef.current[layerId];
          if (originalIconImage !== undefined && map.getLayer(layerId)) {
            map.setLayoutProperty(layerId, "icon-image", originalIconImage);
          }
        });
        originalIconImagesRef.current = {};
      }
      return;
    }

    const map = mapFacade.getMapPop();
    if (!map) {
      setJpbBufferPointLegendLayerId(undefined);
      return;
    }

    const layerHandle = layerInfo.handle;
    const layerIds = layerHandle.getLayerIds();
    const pinPrefix = SettingsManager.getSystemSetting("pinPrefix", "mit-pin");

    layerIds.forEach((layerId) => {
      const layer = map.getLayer(layerId);
      if (layer && layer.type === "symbol") {
        if (!(layerId in originalIconImagesRef.current)) {
          const currentIconImage = map.getLayoutProperty(layerId, "icon-image");
          originalIconImagesRef.current[layerId] = currentIconImage;
        }
        const newIconImage = `${pinPrefix}-${JPB_BUFFER_POINT_PIN_COLOR}`;
        map.setLayoutProperty(layerId, "icon-image", newIconImage);
      }
    });

    setJpbBufferPointLegendLayerId(selectedLayer);

    return () => {
      setJpbBufferPointLegendLayerId(undefined);
      const cleanupMap = mapFacade?.getMapPop();
      if (cleanupMap) {
        Object.keys(originalIconImagesRef.current).forEach((layerId) => {
          const originalIconImage = originalIconImagesRef.current[layerId];
          if (originalIconImage !== undefined && cleanupMap.getLayer(layerId)) {
            cleanupMap.setLayoutProperty(
              layerId,
              "icon-image",
              originalIconImage,
            );
          }
        });
        originalIconImagesRef.current = {};
      }
    };
  }, [mapFacade, selectedLayer, layers]);
  // MARK: RETURN
  const handleClose = () => {
    clearJPBSession(sessionKey);
    windowDispatch(closeWindow(managedWindow.id));
  };

  return (
    <AdvancedDragModal
      title={Localization.getText("Land Price Calculator")}
      window={managedWindow}
      allowUserMiniMize={true}
      allowPopout={true}
      allowFullscreen={true}
      onMinimizedChange={handleMinimizedChange}
      className={
        "JPBModule" + (jpbViewMode === "advanced" ? " jpb-view-advanced" : "")
      }
      // Default: dock to the right, tall window (so it doesn't start tiny and rely on content growth)
      PosDefault={{
        top: "12px",
        left: "calc(100% - 12px)",
        transform: "translateX(-100%)",
        width: "1100px",
      }}
      PositionToSave={{ left: true, top: true, width: true, height: true }}
      layoutConstraintKey={jpbViewMode}
      subHeaderContent={
        jpbViewMode === "simple" ? (
          <JPBSimpleSearchHeader
            mapFacade={mapFacade}
            onPinLayerReady={handleSelectedLayerChange}
            onShowAdvanced={() => {
              patchSearch({ viewMode: "advanced" });
              persistJPBUserSetting("jpb.viewMode", "advanced");
            }}
            resolvedAddressLine={jpbSimpleResolvedAddressLine}
            addressLineResolving={jpbSimpleAddressLineResolving}
            setResolvedAddressLine={setJpbSimpleResolvedAddressLine}
            setAddressLineResolving={setJpbSimpleAddressLineResolving}
          />
        ) : (
          <JPBSearchArea
            selectableLayers={selectableLayers}
            onBufferGeometryChange={handleBufferGeometryChange}
            onSelectedLayerChange={handleSelectedLayerChange}
            controlledLayerSelection={true}
            controlledSelectedLayerId={selectedLayer}
            controlledBufferDistanceKm={bufferDistanceKm}
            controlledBufferDistanceByLayer={bufferDistanceByLayer}
            onBufferDistanceChange={handleBufferDistanceChange}
            propertiesCount={referenceProperties.length}
            areaUnit={areaUnit}
            onAreaUnitChange={handleAreaUnitChange}
            priceMode={priceMode}
            onPriceModeChange={handlePriceModeChange}
            averageCalculationMethod={averageCalculationMethod}
            onAverageCalculationMethodChange={
              handleAverageCalculationMethodChange
            }
            priceIndexSource={priceIndexSource}
            onPriceIndexSourceChange={handlePriceIndexSourceChange}
            onOpenPriceIndexSource={() => {
              windowDispatch(
                openWindow(WindowId.JPBPriceIndexSource, {
                  activeLandsdele: activeMolbakIndexLandsdele,
                }),
              );
            }}
            onOpenTimeHorizon={() => {
              windowDispatch(
                openWindow(WindowId.JPBTimeHorizon, {
                  calculationDateTo: appliedCalculationDateTo,
                  yearGrouping: appliedYearGrouping,
                  minCalculationDateTo: minCalculationDateTo ?? undefined,
                  maxCalculationDateTo: dateBounds?.maxDate,
                  availableYearGroupings,
                  onConfirm: (date, grouping) => {
                    patchFilters({
                      appliedCalculationDateTo: date,
                      appliedYearGrouping: grouping,
                    });
                  },
                }),
              );
            }}
            onExportReport={handleExportReport}
            reportAvailable={
              polygonAnalysis !== null ||
              // Allow auto-trigger of polygon analysis when a point layer
              // is selected: the export handler will run the analysis on
              // the buffered point layer before generating the report.
              (selectedLayer !== undefined &&
                !!layers[selectedLayer] &&
                MapitUtils.isPointLayer(layers[selectedLayer].type))
            }
            isGeneratingReport={isGeneratingReport}
            persistUserSettings={persistUserSettings}
            onPersistUserSettingsChange={handlePersistUserSettingsChange}
            settingsRefreshToken={settingsRefreshToken}
            onOpenSettings={() => {
              windowDispatch(
                openWindow(WindowId.UserSettingsEditor, {
                  filter: (g: { key: string }) => g.key === "jpb",
                }),
              );
            }}
            onShowSimpleView={() => {
              patchSearch({ viewMode: "simple" });
              persistJPBUserSetting("jpb.viewMode", "simple");
            }}
          />
        )
      }
      onClose={() => {
        handleClose();
      }}
    >
      <ADModalBody>
        {/* Loading overlay */}
        {loading && (
          <div className="jpb-window-loading-overlay">
            <div className="jpb-window-loading-content">
              <Spinner
                animation="border"
                variant="light"
                style={{ width: "3rem", height: "3rem" }}
              />
              <p className="jpb-window-loading-text">
                {Localization.getText("Fetching reference transactions...")}
              </p>
            </div>
          </div>
        )}
        {!bufferGeometry && (
          <div className="jpb-window-empty-hint" role="status">
            <p>
              {jpbViewMode === "simple"
                ? Localization.getText(
                  "Search for an address or BFE number above",
                )
                : Localization.getText(
                  "Choose a layer and set the buffer to search for reference transactions",
                )}
            </p>
          </div>
        )}

        {error && (
          <div className="PropertyWarning">
            <p>
              {Localization.getText("Error fetching data:")} {error}
            </p>
          </div>
        )}

        {!loading &&
          !error &&
          bufferGeometry &&
          referenceProperties.length === 0 && (
            <p>{Localization.getText("No reference transactions found")}</p>
          )}
        {jpbViewMode === "advanced" && bufferGeometry && (
          <div ref={histogramRef}>
            <JPBHistogram
              data={filteredDataForHistogram}
              allData={referenceProperties}
              currentMin={customPriceAssumptions?.pricePerMeterMin}
              currentMax={customPriceAssumptions?.pricePerMeterMax}
              onRangeChange={handlePriceRangeChange}
              onResetToSystem={handleResetToSystemAssumptions}
              areaUnit={areaUnit}
              priceMode={priceMode}
              averageCalculationMethod={averageCalculationMethod}
            />
          </div>
        )}
        {jpbViewMode === "advanced" &&
          referenceProperties.length > 1 &&
          checkedItems.size > 1 && (
            <div ref={statisticsRef}>
              <JPBStatistics
                data={referenceProperties}
                areaUnit={areaUnit}
                priceMode={priceMode}
                checkedItems={checkedItems}
                yearGrouping={appliedYearGrouping}
                calculationDateTo={appliedCalculationDateTo ?? undefined}
                indexMaxTransactionDate={dateBounds?.maxDate}
                averageCalculationMethod={averageCalculationMethod}
                priceIndexSource={priceIndexSource}
                forceSoilExpanded={forceStatisticsSoilExpanded || undefined}
              />
            </div>
          )}
        {((jpbViewMode === "simple" && selectedLayer !== undefined) ||
          (jpbViewMode === "advanced" && referenceProperties.length > 0)) && (
            <ProtectedFeature
              feature={Feature.JPBSimulator}
              contentsIfNoAccess={<></>}
            >
              <div ref={polygonAnalyzerRef}>
                <JPBPolygonAnalyzer
                  referenceProperties={referenceProperties}
                  checkedItems={checkedItems}
                  areaUnit={areaUnit}
                  averageCalculationMethod={averageCalculationMethod}
                  customAssumptions={customPriceAssumptions}
                  settingsRefreshToken={settingsRefreshToken}
                  lockedAnalyzerLayerId={
                    jpbViewMode === "simple" && selectedLayer !== undefined
                      ? selectedLayer
                      : undefined
                  }
                  subjectAddress={
                    jpbViewMode === "simple"
                      ? (jpbSimpleResolvedAddressLine ?? undefined)
                      : selectedLayer !== undefined
                        ? ((layers[selectedLayer] as any)?.layer?.label ??
                          (layers[selectedLayer] as any)?.layer?.name)
                        : undefined
                  }
                  onAnalysisChange={setPolygonAnalysis}
                />
              </div>
            </ProtectedFeature>
          )}
        {jpbViewMode === "advanced" && referenceProperties.length > 0 && (
          <>
            <div ref={tableScrollAnchorRef} />
            <JPBTable
              ref={tableRef}
              data={tableData}
              checkedItems={checkedItems}
              onCheckedItemsChange={updateCheckedItems}
              onRowClick={handleRowClick}
              yearGrouping={appliedYearGrouping}
              averageCalculationMethod={averageCalculationMethod}
              calculationDateTo={appliedCalculationDateTo ?? undefined}
              indexMaxTransactionDate={dateBounds?.maxDate}
              hideUnselected={hideUnselected}
              onHideUnselectedChange={handleHideUnselectedChange}
              customAssumptions={customPriceAssumptions}
              onCustomAssumptionsChange={handleCustomAssumptionsChange}
              areaUnit={areaUnit}
              priceMode={priceMode}
              priceIndexSource={priceIndexSource}
            />
          </>
        )}
      </ADModalBody>
    </AdvancedDragModal>
  );
}

const MemoizedJPBWindowInner = memo(
  JPBWindowInner,
  (prev, next) =>
    prev.map === next.map &&
    prev.layers === next.layers &&
    prev.managedWindow === next.managedWindow &&
    prev.restoredFromSkipPersist === next.restoredFromSkipPersist,
);

export function JPBWindow(props: { managedWindow: ManagedWindow }) {
  const { state: mapitState, dispatch } = useContext(MapitStateContext);
  const { state: sessionState } = useContext(SessionContext);
  const { hasAccessToFeature } = useContext(ApplicationStateContext);
  const mapInfoRef = useRef<{ mapStyle?: any } | null>(null);
  mapInfoRef.current = mapitState.mapInfo;
  const [openPrep] = useState(() => prepareJPBWindowOpen());
  const getDefaultAccessiblePriceIndexSource = (): JPBPriceIndexSource => {
    if (hasAccessToFeature(Feature.JPBMolbakIndexAllow))
      return JPBPriceIndexSource.Molbak;
    if (hasAccessToFeature(Feature.JPBAgroCuraIndexAllow))
      return JPBPriceIndexSource.AgroCura;
    return JPBPriceIndexSource.None;
  };
  const initialSessionState = useMemo(
    () =>
      createJPBInitialSessionState(
        getDefaultAccessiblePriceIndexSource,
        openPrep.useDefaultSessionChoices,
      ),
    [hasAccessToFeature, openPrep.useDefaultSessionChoices],
  );

  useEffect(() => {
    if (!openPrep.useDefaultSessionChoices) return;
    if (!sessionState.customerRef || !sessionState.userRef) return;
    const remaining = SettingsManager.extractSettingsByPriority(
      SettingsFixedPriorities.UserSettings,
      SettingsFixedPriorities.UserSettings,
      true,
    );
    persistUserSettingsProfile(
      remaining,
      sessionState.customerRef,
      sessionState.userRef,
    ).catch((error) => {
      console.error("Failed to persist reset JPB user settings", error);
    });
  }, [
    openPrep.useDefaultSessionChoices,
    sessionState.customerRef,
    sessionState.userRef,
  ]);

  const sessionKey = props.managedWindow.uid ?? props.managedWindow.id;
  return (
    <JPBSessionProvider
      sessionKey={sessionKey}
      initialState={initialSessionState}
    >
      <MemoizedJPBWindowInner
        managedWindow={props.managedWindow}
        map={mapitState.map}
        layers={mapitState.layers}
        dispatch={dispatch}
        mapInfoRef={mapInfoRef}
        restoredFromSkipPersist={openPrep.useDefaultSessionChoices}
      />
    </JPBSessionProvider>
  );
}

export default JPBWindow;
